import { useMutation } from "@tanstack/react-query";

import { queryClient } from "@/config/react-query";

import { toast } from "@/lib/use-toast";

import type { DocumentSummary } from "@/types/document";
import type { NoteDetail } from "@/types/notes";
import type { PatientSidebarCounts } from "@/types/patient-sidebar";

import {
  deleteDocument,
  sendDocumentText,
  setDocumentExpiry,
  shareDocumentWithPatient,
  signOffDocument,
  updateDocument,
  updateDocumentReviewComment,
  uploadDocument,
} from "./document";
import { documentQueryKeys } from "./document-query";
import {
  optimisticAdjustSidebarCounts,
  revertSidebarCounts,
} from "./sidebar-cache-utils";
import { phlebotomyOrderQueryKeys } from "@/api/provider/phlebotomy-order/phlebotomy-orders-query";
import {
  linkDocumentsToNote,
  unlinkDocumentFromNote,
} from "@/api/provider/scribe/note-documents";
import { noteQueryKeys } from "@/api/provider/scribe/note-query";

const patchDocumentInNoteCaches = (doc: DocumentSummary) => {
  const attachedNoteIds = Array.from(new Set(doc.noteIds ?? []));
  for (const noteId of attachedNoteIds) {
    queryClient.setQueryData<DocumentSummary[] | null>(
      noteQueryKeys.noteDocuments(noteId),
      (oldData) =>
        oldData?.map((document) =>
          document.id === doc.id ? { ...document, ...doc } : document,
        ) ?? oldData,
    );
  }

  if (doc.sourceNoteId) {
    queryClient.setQueryData<NoteDetail | undefined>(
      noteQueryKeys.detail(doc.sourceNoteId),
      (oldData) =>
        oldData
          ? {
              ...oldData,
              sourceDocuments: oldData.sourceDocuments.map((document) =>
                document.id === doc.id ? { ...document, ...doc } : document,
              ),
            }
          : oldData,
    );
  }
};

export const useUploadDocumentMutation = () =>
  useMutation<
    DocumentSummary | null,
    Error,
    {
      patientId: string;
      file: File;
      title: string;
      docType: string;
      summary?: string;
      expiryDate?: string | null;
    },
    { previousSidebarCounts?: PatientSidebarCounts }
  >({
    mutationFn: ({ patientId, file, title, docType, summary, expiryDate }) =>
      uploadDocument(patientId, file, title, docType, summary, expiryDate),
    onMutate: async ({ patientId }) => {
      const previousSidebarCounts = optimisticAdjustSidebarCounts(patientId, {
        documentsCount: 1,
      });
      return { previousSidebarCounts };
    },
    onError: (_err, vars, ctx) => {
      revertSidebarCounts(vars.patientId, ctx?.previousSidebarCounts);
    },
    onSuccess: (_doc, vars) => {
      queryClient.invalidateQueries({
        queryKey: documentQueryKeys.list(vars.patientId),
      });
    },
  });

export const useDeleteDocumentMutation = () =>
  useMutation<
    boolean | null,
    Error,
    { documentId: string; patientId: string },
    { previousSidebarCounts?: PatientSidebarCounts }
  >({
    mutationFn: ({ documentId }) => deleteDocument(documentId),
    onMutate: async ({ patientId }) => {
      const previousSidebarCounts = optimisticAdjustSidebarCounts(patientId, {
        documentsCount: -1,
      });
      return { previousSidebarCounts };
    },
    onError: (_err, vars, ctx) => {
      revertSidebarCounts(vars.patientId, ctx?.previousSidebarCounts);
      toast({
        title: "Error",
        description: "Failed to delete document",
        variant: "error",
      });
    },
    onSuccess: (_ok, vars) => {
      queryClient.invalidateQueries({
        queryKey: documentQueryKeys.list(vars.patientId),
      });
    },
  });

export const useUpdateDocumentMutation = () =>
  useMutation<
    DocumentSummary | null,
    Error,
    { documentId: string; patientId: string; title: string; docType: string }
  >({
    mutationFn: ({ documentId, title, docType }) =>
      updateDocument(documentId, title, docType),
    onSuccess: (doc, vars) => {
      queryClient.invalidateQueries({
        queryKey: documentQueryKeys.list(vars.patientId),
      });
      queryClient.invalidateQueries({
        queryKey: documentQueryKeys.idList(vars.patientId),
      });
      if (doc) patchDocumentInNoteCaches(doc);
    },
  });

export const useSignOffDocumentMutation = () =>
  useMutation<
    DocumentSummary | null,
    Error,
    { documentId: string; patientId: string; comment?: string }
  >({
    mutationFn: ({ documentId, comment }) =>
      signOffDocument(documentId, comment),
    onSuccess: (doc, vars) => {
      queryClient.invalidateQueries({
        queryKey: documentQueryKeys.list(vars.patientId),
      });
      if (doc) patchDocumentInNoteCaches(doc);
      toast({
        title: "Document signed off",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to sign off document",
        variant: "error",
      });
    },
  });

export const useUpdateDocumentReviewCommentMutation = () =>
  useMutation<
    DocumentSummary | null,
    Error,
    { documentId: string; patientId: string; comment: string }
  >({
    mutationFn: ({ documentId, comment }) =>
      updateDocumentReviewComment(documentId, comment),
    onSuccess: (doc, vars) => {
      queryClient.invalidateQueries({
        queryKey: documentQueryKeys.list(vars.patientId),
      });
      if (doc) patchDocumentInNoteCaches(doc);
      toast({
        title: "Note saved",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to save note",
        variant: "error",
      });
    },
  });

/**
 * Sharing a document to the portal — and texting the patient a link, which sets
 * the same two columns — is send evidence, and a phlebotomy order's Sent status
 * is derived from it. That list is a separate query key, so without this the
 * chip stays stale until the page is reloaded.
 */
const invalidateDerivedSendStatus = (patientId: string) => {
  queryClient.invalidateQueries({
    queryKey: phlebotomyOrderQueryKeys.list(patientId),
  });
};

export const useShareDocumentMutation = () =>
  useMutation<
    DocumentSummary | null,
    Error,
    { documentId: string; patientId: string; shared: boolean }
  >({
    mutationFn: ({ documentId, shared }) =>
      shareDocumentWithPatient(documentId, shared),
    onSuccess: (doc, vars) => {
      queryClient.invalidateQueries({
        queryKey: documentQueryKeys.list(vars.patientId),
      });
      invalidateDerivedSendStatus(vars.patientId);
      if (doc) patchDocumentInNoteCaches(doc);
      toast({
        title: vars.shared
          ? "Shared to patient portal"
          : "Removed from patient portal",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update portal sharing",
        variant: "error",
      });
    },
  });

export const useSendDocumentTextMutation = () =>
  useMutation<
    DocumentSummary | null,
    Error,
    { documentId: string; patientId: string; message: string }
  >({
    mutationFn: ({ documentId, message }) =>
      sendDocumentText(documentId, message),
    onSuccess: (doc, vars) => {
      queryClient.invalidateQueries({
        queryKey: documentQueryKeys.list(vars.patientId),
      });
      invalidateDerivedSendStatus(vars.patientId);
      if (doc) patchDocumentInNoteCaches(doc);
      toast({
        title: "Texted to patient",
        variant: "success",
      });
    },
    onError: (err) => {
      toast({
        title: "Couldn't text the patient",
        description: err.message || "Please try again.",
        variant: "error",
      });
    },
  });

export const useSetDocumentExpiryMutation = () =>
  useMutation<
    DocumentSummary | null,
    Error,
    { documentId: string; patientId: string; expiryDate: string | null }
  >({
    mutationFn: ({ documentId, expiryDate }) =>
      setDocumentExpiry(documentId, expiryDate),
    onSuccess: (doc, vars) => {
      queryClient.invalidateQueries({
        queryKey: documentQueryKeys.list(vars.patientId),
      });
      if (doc) patchDocumentInNoteCaches(doc);
      toast({
        title: "Success",
        description: "Expiry date updated",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update expiry date",
        variant: "error",
      });
    },
  });

export const useManageDocumentNoteLinksMutation = () =>
  useMutation<
    unknown,
    Error,
    {
      documentId: string;
      patientId: string;
      noteIdsToLink: string[];
      noteIdsToUnlink: string[];
    }
  >({
    mutationFn: async ({ documentId, noteIdsToLink, noteIdsToUnlink }) => {
      await Promise.all([
        ...noteIdsToLink.map((noteId) =>
          linkDocumentsToNote(noteId, [documentId]),
        ),
        ...noteIdsToUnlink.map((noteId) =>
          unlinkDocumentFromNote(noteId, documentId),
        ),
      ]);
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: documentQueryKeys.list(vars.patientId),
      });
      for (const noteId of [...vars.noteIdsToLink, ...vars.noteIdsToUnlink]) {
        queryClient.invalidateQueries({
          queryKey: noteQueryKeys.noteDocuments(noteId),
        });
      }
      toast({
        title: "Success",
        description: "Note links updated",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update note links",
        variant: "error",
      });
    },
  });
